'use client';

import type { Route } from 'next';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback } from 'react';

export interface RouterPush {
	(pathname: string, queryParams?: Record<string, string | undefined | null>): void;
}

export function useRouterPush() {
	const router = useRouter();
	const currentPathname = usePathname();
	const currentParams = useSearchParams();

	return useCallback(
		(pathname: string, queryParams: Record<string, string | undefined | null> = {}) => {
			const currentQueryString = currentParams?.toString() ?? '';
			const params = new URLSearchParams(currentQueryString);

			// Merge new params
			Object.entries(queryParams).forEach(([key, value]) => {
				if (value === undefined || value === null) {
					params.delete(key);
				} else {
					params.set(key, value);
				}
			});

			const queryString = params.toString();
			const url = queryString ? `${pathname}?${queryString}` : pathname;

			// Skip the push when the target URL is identical to the current URL.
			// Pages Router throws "Invariant: attempted to hard navigate to the same URL"
			// when router.push is called with the current path and the route triggers
			// a hard navigation (e.g. via the client router bloom filter).
			if (pathname === currentPathname && queryString === currentQueryString) {
				return;
			}

			return router.push(url as Route);
		},
		[router, currentPathname, currentParams],
	);
}
